import React, { useMemo } from 'react';
import {
    Column,
    formatThousand,
    IconButton,
    useSafeIntl,
} from 'bluesquare-components';
import { UserCell } from 'Iaso/components/Cells/UserCell';
import { ProjectChips } from 'Iaso/domains/projects/components/ProjectChips';
import { DateTimeCell } from '../../../components/Cells/DateTimeCell';
import { baseUrls } from '../../../constants/urls';
import * as Permission from '../../../utils/permissions';
import { useCurrentUser, User } from '../../../utils/usersUtils';
import {
    userHasAccessToModule,
    userHasOneOfPermissions,
    userHasPermission,
} from '../../users/utils';
import { FormActions } from '../components/FormActions';
import FormVersionsDialog from '../components/FormVersionsDialogComponent';
import MESSAGES from '../messages';
import { FormsParams } from '../types/forms';
import { useDeleteForm } from '../hooks/useDeleteForm';

export const baseUrl = baseUrls.forms;

type FormVersionsTableColumnsProps = {
    formId: string;
    periodType: string;
};

export const useFormVersionsTableColumns = ({
    formId,
    periodType,
}: FormVersionsTableColumnsProps): Column[] => {
    const { formatMessage } = useSafeIntl();
    return useMemo(
        () => [
            {
                Header: formatMessage(MESSAGES.version),
                accessor: 'version_id',
            },
            {
                Header: formatMessage(MESSAGES.startPeriod),
                accessor: 'start_period',
            },
            {
                Header: formatMessage(MESSAGES.endPeriod),
                accessor: 'end_period',
            },
            {
                Header: formatMessage(MESSAGES.created_by),
                id: 'created_by__username',
                accessor: 'created_by',
                Cell: UserCell,
            },
            {
                Header: formatMessage(MESSAGES.created_at),
                id: 'created_at',
                accessor: 'created_at',
                Cell: DateTimeCell,
            },
            {
                Header: formatMessage(MESSAGES.updated_by),
                id: 'updated_by__username',
                accessor: 'updated_by',
                Cell: UserCell,
            },
            {
                Header: formatMessage(MESSAGES.updated_at),
                id: 'updated_at',
                accessor: 'updated_at',
                Cell: DateTimeCell,
            },
            {
                Header: formatMessage(MESSAGES.actions),
                accessor: 'actions',
                sortable: false,
                Cell: settings => {
                    return (
                        <section>
                            {settings.row.original.xls_file && (
                                <IconButton
                                    url={settings.row.original.xls_file}
                                    download
                                    icon="xls"
                                    tooltipMessage={MESSAGES.xls_form_file}
                                    reloadDocument
                                />
                            )}
                            <FormVersionsDialog
                                renderTrigger={({ openDialog }) => (
                                    <IconButton
                                        onClick={openDialog}
                                        icon="edit"
                                        tooltipMessage={MESSAGES.edit}
                                    />
                                )}
                                formVersion={settings.row.original}
                                periodType={periodType}
                                formId={parseInt(formId, 10)}
                                titleMessage={{
                                    ...MESSAGES.updateFormVersion,
                                    values: {
                                        version_id:
                                            settings.row.original.version_id,
                                    },
                                }}
                                key={settings.row.original.updated_at}
                            />
                        </section>
                    );
                },
            },
        ],
        [formId, formatMessage, periodType],
    );
};

const getActionsColWidth = (user: User): number => {
    const baseWidth = 50;
    let width = baseWidth * 2;
    if (
        userHasOneOfPermissions(
            [Permission.SUBMISSIONS, Permission.SUBMISSIONS_UPDATE],
            user,
        )
    ) {
        width += baseWidth;
    }
    if (userHasPermission(Permission.FORMS, user)) {
        width += baseWidth * 3;
    }
    return width;
};

type FormsTableColumnsProps = {
    orgUnitId: string;
    showDeleted: boolean;
    params: FormsParams;
    count: number;
};

export const useFormsTableColumns = ({
    orgUnitId,
    showDeleted,
    params,
    count,
}: FormsTableColumnsProps): Column[] => {
    const user = useCurrentUser();
    const hasDhis2Module = userHasAccessToModule('DHIS2_MAPPING', user);

    const { formatMessage } = useSafeIntl();

    const { mutateAsync: deleteForm } = useDeleteForm({ params, count }); 


    return useMemo(() => {
        const cols: Column[] = [
            {
                Header: formatMessage(MESSAGES.projects),
                id: 'projects',
                accessor: 'projects',
                Cell: settings => <ProjectChips projects={settings.value} />,
            },
            {
                Header: formatMessage(MESSAGES.name),
                id: 'name',
                accessor: 'name',
                align: 'left' as const,
            },
            {
                Header: formatMessage(MESSAGES.created_at),
                id: 'created_at',
                accessor: 'created_at',
                Cell: DateTimeCell,
            },
            {
                Header: formatMessage(MESSAGES.updated_at),
                accessor: 'updated_at',
                id: 'updated_at',
                Cell: DateTimeCell,
            },
            {
                Header: formatMessage(MESSAGES.instance_updated_at),
                accessor: 'instance_updated_at',
                id: 'instance_updated_at',
                Cell: DateTimeCell,
            },
            {
                Header: formatMessage(MESSAGES.type),
                sortable: false,
                accessor: 'org_unit_types',
                id: 'org_unit_types',
                Cell: settings =>
                    settings.row.original.org_unit_types
                        .map(o => o.short_name)
                        .join(', '),
            },
            {
                Header: formatMessage(MESSAGES.instances_count),
                id: 'instances_count',
                accessor: 'instances_count',
                Cell: settings => formatThousand(settings.value),
            },
            {
                Header: formatMessage(MESSAGES.actions),
                resizable: false,
                sortable: false,
                width: getActionsColWidth(user),
                id: 'actions',
                accessor: 'actions',
                Cell: settings => {
                    return (
                        <FormActions
                            settings={settings}
                            orgUnitId={orgUnitId}
                            baseUrls={baseUrls}
                            showDeleted={showDeleted}
                            hasDhis2Module={hasDhis2Module}
                            deleteForm={deleteForm}
                        />
                    );
                },
            },
        ];
        if (showDeleted) {
            cols.splice(1, 0, {
                Header: formatMessage(MESSAGES.deleted_at),
                id: 'deleted_at',
                accessor: 'deleted_at',
                Cell: DateTimeCell,
            });
        }
        return cols;
    }, [formatMessage, user, showDeleted, orgUnitId, hasDhis2Module, deleteForm]);
};

export const requiredFields = [
    {
        type: 'string',
        key: 'name',
    },
    {
        type: 'array',
        key: 'project_ids',
    },
    {
        type: 'string',
        key: 'periods_before_allowed',
    },
    {
        type: 'string',
        key: 'periods_after_allowed',
    },
    {
        type: 'boolean',
        key: 'single_per_period',
    },
];
